import proj4 from 'proj4';
import type { Map as MapboxMap } from 'mapbox-gl';
import { GpsCoordinates } from '../types';

type MultiPolygon = {
  type: 'MultiPolygon';
  coordinates: number[][][][];
};

type Bbox = [number, number, number, number];

export const getCentroidCoordinates = (coordinates: GpsCoordinates[]): GpsCoordinates => {
  let totalX = 0;
  let totalY = 0;
  let totalZ = 0;

  coordinates.forEach(([lng, lat]) => {
    const radLat = lat ? (lat * Math.PI) / 180 : 0;
    const radLng = lng ? (lng * Math.PI) / 180 : 0;

    totalX += Math.cos(radLat) * Math.cos(radLng);
    totalY += Math.cos(radLat) * Math.sin(radLng);
    totalZ += Math.sin(radLat);
  });

  const numPoints = coordinates.length;
  totalX /= numPoints;
  totalY /= numPoints;
  totalZ /= numPoints;

  const lng = Math.atan2(totalY, totalX) * (180 / Math.PI);
  const hyp = Math.sqrt(totalX * totalX + totalY * totalY);
  const lat = Math.atan2(totalZ, hyp) * (180 / Math.PI);

  return [lng, lat];
};

export const removePolygonSources = (
  id: string,
  layerFillId: string,
  layerOutlineId: string,
  map: MapboxMap,
): void => {
  const source = map.getSource(id);
  if (source) {
    map.removeLayer(layerFillId);
    map.removeLayer(layerOutlineId);
    map.removeSource(id);
  }
};

export const multiPolygonToEPSG3857 = (multipolygon: MultiPolygon): MultiPolygon => {
  const transformedMultipolygon: MultiPolygon = JSON.parse(JSON.stringify(multipolygon));

  transformedMultipolygon.coordinates.forEach((polygon) => {
    polygon.forEach((ring) => {
      ring.forEach((coord) => {
        const [lng, lat] = proj4('EPSG:4326', 'EPSG:3857', [coord[0], coord[1]]);
        coord[0] = lng;
        coord[1] = lat;
      });
    });
  });

  return transformedMultipolygon;
};

export const multiPolygonToWKT = (multipolygon: MultiPolygon): string =>
  `MULTIPOLYGON (${multipolygon.coordinates
    .map(
      (polygon) =>
        `(${polygon.map((ring) => `(${ring.map((coord) => coord.join(' ')).join(', ')})`).join(', ')})`,
    )
    .join(', ')})`;

export const getBboxFromMultipolygon = (multipolygon: MultiPolygon): Bbox => {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  multipolygon.coordinates.forEach((polygon) => {
    polygon.forEach((ring) => {
      ring.forEach((coord) => {
        const [lng, lat] = coord;
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      });
    });
  });

  return [minLng, minLat, maxLng, maxLat];
}